import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import CookieBanner from "@/components/CookieBanner";
import CTASection from "@/components/CTASection";
import ExpertSection from "@/components/ExpertSection";

import HeroSection from "@/components/layout/HeroSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Shield, AlertTriangle, Lock, Wifi, Database, Mail, Phone, Server, Eye, FileCode, Users, Smartphone, Cloud, CreditCard, Video, MessageSquare, Network, HardDrive, Bug, Key, Calendar, MessageCircle } from "lucide-react";
import CenteredWarningBox from "@/components/CenteredWarningBox";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import cyberRisksHero from "@/assets/cyber-risks-hero.jpg";
import { useImagePreload } from "@/hooks/useImagePreload";
const CyberRisks = () => {
  useImagePreload(cyberRisksHero);
  const risks = [{
    icon: Lock,
    title: "Ransomware",
    description: "Schadsoftware, die Ihre Daten verschlüsselt und Lösegeld fordert. Ransomware-Angriffe können den gesamten Praxisbetrieb zum Stillstand bringen und zu massiven finanziellen Verlusten führen.",
    severity: "Kritisch",
    color: "destructive"
  }, {
    icon: Mail,
    title: "Phishing",
    description: "Gefälschte E-Mails, die Mitarbeiter täuschen und zur Preisgabe vertraulicher Informationen verleiten. Phishing ist die häufigste Angriffsmethode und Einfallstor für weitere Cyberangriffe.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: Users,
    title: "Insider-Bedrohung",
    description: "Mitarbeiter geben absichtlich oder fahrlässig sensible Daten weiter. Dies kann durch unzureichende Schulung, Unachtsamkeit oder böswillige Absicht geschehen.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: Bug,
    title: "Trojaner",
    description: "Getarnte Schadsoftware, die sich als harmlose Datei ausgibt. Trojaner ermöglichen Hackern Fernzugriff auf Ihre Systeme und das Ausspähen sensibler Patientendaten.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: Database,
    title: "SQL-Injection",
    description: "Angriff auf Datenbanken durch Ausnutzung von Sicherheitslücken. SQL-Injection ermöglicht unbefugten Zugriff auf Patientendaten und kann zur Manipulation oder Löschung führen.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: Network,
    title: "Man-in-the-Middle",
    description: "Abfangen und Manipulieren der Kommunikation zwischen zwei Parteien. Besonders gefährlich bei unverschlüsselter Datenübertragung von Patienteninformationen.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: Server,
    title: "DDoS-Angriff",
    description: "Überlastung Ihrer IT-Systeme durch massive Anfragen. DDoS-Angriffe können Ihre Online-Services, Terminbuchung und Praxis-Website tagelang lahmlegen.",
    severity: "Mittel",
    color: "warning"
  }, {
    icon: Key,
    title: "Brute-Force-Angriff",
    description: "Systematisches Erraten von Passwörtern durch automatisierte Programme. Zielt auf schwache Passwörter ab und ermöglicht unbefugten Zugang zu kritischen Systemen.",
    severity: "Mittel",
    color: "warning"
  }, {
    icon: Phone,
    title: "CEO Fraud",
    description: "Betrüger geben sich als Führungskraft aus und fordern Mitarbeiter zu Überweisungen auf. Diese Social-Engineering-Methode führt oft zu erheblichen finanziellen Verlusten.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: Video,
    title: "Deepfake",
    description: "KI-generierte gefälschte Videos oder Audiodateien zur Täuschung. Deepfakes können für Betrug, Erpressung oder zur Rufschädigung eingesetzt werden.",
    severity: "Mittel",
    color: "warning"
  }, {
    icon: Eye,
    title: "Spyware",
    description: "Überwachungssoftware, die heimlich Daten sammelt und an Dritte sendet. Spyware kann Passwörter, Kreditkartendaten und vertrauliche Patienteninformationen ausspähen.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: Wifi,
    title: "Unsichere WLAN-Netze",
    description: "Ungeschützte oder schlecht gesicherte WLAN-Verbindungen ermöglichen Angreifern den Zugriff auf Ihr Netzwerk und sensible Daten.",
    severity: "Mittel",
    color: "warning"
  }, {
    icon: Cloud,
    title: "Cloud-Sicherheitslücken",
    description: "Fehlkonfigurationen oder Schwachstellen in Cloud-Diensten können zu Datenlecks führen. Besonders kritisch bei der Speicherung von Patientendaten.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: Smartphone,
    title: "Mobile Malware",
    description: "Schadsoftware auf Smartphones und Tablets, die zunehmend in Praxen genutzt werden. Mobile Geräte sind oft weniger geschützt als stationäre Computer.",
    severity: "Mittel",
    color: "warning"
  }, {
    icon: CreditCard,
    title: "Zahlungsbetrug",
    description: "Manipulation von Zahlungssystemen und Abrechnungsprozessen. Kann zu finanziellen Verlusten und Vertrauensverlust bei Patienten führen.",
    severity: "Mittel",
    color: "warning"
  }, {
    icon: HardDrive,
    title: "Datenverlust",
    description: "Versehentliches oder böswilliges Löschen wichtiger Daten ohne ausreichende Backups. Kann zu irreversiblem Verlust kritischer Patienteninformationen führen.",
    severity: "Hoch",
    color: "destructive"
  }, {
    icon: FileCode,
    title: "Zero-Day-Exploits",
    description: "Ausnutzung unbekannter Sicherheitslücken in Software. Besonders gefährlich, da noch keine Sicherheitsupdates verfügbar sind.",
    severity: "Kritisch",
    color: "destructive"
  }, {
    icon: MessageSquare,
    title: "Social Engineering",
    description: "Manipulation von Mitarbeitern zur Preisgabe sensibler Informationen. Nutzt menschliche Schwächen statt technischer Sicherheitslücken.",
    severity: "Hoch",
    color: "destructive"
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "Kritisch":
        return "text-red-600 bg-red-600/10 border-red-600/30";
      case "Hoch":
        return "text-destructive bg-destructive/10 border-destructive/30";
      case "Mittel":
        return "text-yellow-600 bg-yellow-600/10 border-yellow-600/30";
      default:
        return "text-foreground/70 bg-muted border-border";
    }
  };
  return <div className="min-h-screen relative">
      <div className="relative z-10">
        <Navigation />
        <CookieBanner />
        <PageBreadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "Cyberrisiken" }
        ]} />
      
      {/* Hero Section */}
      <HeroSection
        image={cyberRisksHero}
        alt="Cyberrisiken für Arztpraxen"
        title="Die größten Cyber-Gefahren für medizinische Einrichtungen – Ransomware, Phishing und Datenverlust"
        gradientPreset="none"
        imageOpacity={30}
        overlayColor="background"
        className="py-20 sm:py-24 lg:py-32 px-4"
      >
        <div className="container mx-auto max-w-4xl text-center relative z-10 animate-fade-in">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-destructive/10 rounded-full mb-6">
            <AlertTriangle className="w-7 h-7 text-destructive" />
          </div>
          
          <h1 className="heading-1 mb-6 text-foreground leading-tight">
            Cyberrisiken für Arztpraxen
          </h1>
          
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-accent/5 rounded-full text-accent text-sm font-medium">
              Bedrohungen • Prävention • Schutz
            </span>
          </div>
          
          <p className="body-text text-foreground/70 max-w-2xl mx-auto">
            Die digitale Bedrohungslandschaft im Gesundheitswesen - eine umfassende Übersicht 
            aller relevanten Bedrohungen und wie Sie sich davor schützen können.
          </p>
        </div>
      </HeroSection>

      {/* Feature-Boxen mit harter Kante */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center p-4 bg-background/50 rounded-lg border border-destructive/30">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-destructive" />
              <div className="text-2xl font-bold text-destructive mb-1">43%</div>
              <div className="text-sm text-foreground/70">Praxen angegriffen</div>
            </div>
            <div className="text-center p-4 bg-background/50 rounded-lg border border-destructive/30">
              <Shield className="w-8 h-8 mx-auto mb-2 text-destructive" />
              <div className="text-2xl font-bold text-destructive mb-1">68.451€</div>
              <div className="text-sm text-foreground/70">Ø Schadenshöhe</div>
            </div>
            <div className="text-center p-4 bg-background/50 rounded-lg border border-destructive/30">
              <Eye className="w-8 h-8 mx-auto mb-2 text-destructive" />
              <div className="text-2xl font-bold text-destructive mb-1">24/7</div>
              <div className="text-sm text-foreground/70">Bedrohung</div>
            </div>
          </div>
        </div>
      </section>


      {/* Risks Grid */}
      <ResponsiveSection variant="default" spacing="lg" containerSize="lg">
        <h2 className="heading-2 mb-6 sm:mb-8 text-center text-destructive">
          Die wichtigsten Cyberbedrohungen im Überblick
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-responsive-md">
          {risks.map((risk, index) => <div key={index} className="bg-card border border-border rounded-lg card-padding-md hover:shadow-lg transition-all hover:-translate-y-1 animate-fade-in" style={{
            animationDelay: `${index * 50}ms`
          }}>
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <risk.icon className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-foreground text-sm sm:text-base mb-1 sm:mb-2">{risk.title}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full border ${getSeverityColor(risk.severity)}`}>
                    {risk.severity}
                  </span>
                </div>
              </div>
              <p className="text-foreground/70 text-xs sm:text-sm leading-relaxed">
                {risk.description}
              </p>
            </div>)}
        </div>
      </ResponsiveSection>

      {/* Real Damage Examples Section */}
      <ResponsiveSection variant="card" spacing="xl" containerSize="lg">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="heading-1 mb-4 text-foreground">
            Echte Schadensbeispiele
          </h2>
          <p className="body-text text-foreground/70 max-w-3xl mx-auto">
            Dokumentierte Cybervorfälle aus dem deutschen Gesundheitswesen – reale Fälle mit messbaren finanziellen Folgen
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-responsive-md">
          {/* Example 1 */}
          <div className="bg-card border-2 border-destructive/30 rounded-lg card-padding-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm sm:text-base mb-1">Zahnarztpraxis München</h3>
                  <span className="text-xs text-destructive font-semibold">Ransomware-Angriff</span>
                </div>
              </div>
              <p className="text-sm text-foreground/70 mb-4">
                Alle Patientendaten wurden verschlüsselt. Die Praxis war 3 Wochen nicht arbeitsfähig.
                Lösegeldforderung: 45.000€
              </p>
              <div className="pt-4">
                <p className="text-xs text-foreground/60">
                  <strong className="text-destructive">Gesamtschaden:</strong> ca. 85.000€ (inkl. Umsatzausfall)
                </p>
              </div>
            </div>

          {/* Example 2 */}
          <div className="bg-card border-2 border-destructive/30 rounded-lg card-padding-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm sm:text-base mb-1">Hausarztpraxis Hamburg</h3>
                  <span className="text-xs text-destructive font-semibold">Phishing-Angriff</span>
                </div>
              </div>
              <p className="text-sm text-foreground/70 mb-4">
                Mitarbeiterin öffnete gefälschte Email. Hacker erlangten Zugriff auf 2.800 Patientendatensätze.
                DSGVO-Bußgeld wurde verhängt.
              </p>
              <div className="pt-4">
                <p className="text-xs text-foreground/60">
                  <strong className="text-destructive">Gesamtschaden:</strong> ca. 52.000€ (Bußgeld + Anwaltskosten)
                </p>
              </div>
            </div>

          {/* Example 3 */}
          <div className="bg-card border-2 border-destructive/30 rounded-lg card-padding-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Server className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm sm:text-base mb-1">MVZ Berlin</h3>
                  <span className="text-xs text-destructive font-semibold">DDoS-Angriff</span>
                </div>
              </div>
              <p className="text-sm text-foreground/70 mb-4">
                IT-Systeme 5 Tage lahmgelegt. Keine Terminvergabe, keine digitale Patientenakte möglich.
                240 Termine mussten abgesagt werden.
              </p>
              <div className="pt-4">
                <p className="text-xs text-foreground/60">
                  <strong className="text-destructive">Gesamtschaden:</strong> ca. 38.000€ (Umsatzausfall + IT-Kosten)
                </p>
              </div>
            </div>

          {/* Example 4 */}
          <div className="bg-card border-2 border-destructive/30 rounded-lg card-padding-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <HardDrive className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm sm:text-base mb-1">Klinik Köln</h3>
                  <span className="text-xs text-destructive font-semibold">Datenverlust</span>
                </div>
              </div>
              <p className="text-sm text-foreground/70 mb-4">
                Serverausfall ohne funktionierendes Backup. 6 Jahre Patientenhistorie unwiederbringlich verloren.
                Reputationsschaden immens.
              </p>
              <div className="pt-4">
                <p className="text-xs text-foreground/60">
                  <strong className="text-destructive">Gesamtschaden:</strong> ca. 120.000€ (Wiederherstellung + Image)
                </p>
              </div>
            </div>

          {/* Example 5 */}
          <div className="bg-card border-2 border-destructive/30 rounded-lg card-padding-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm sm:text-base mb-1">Gemeinschaftspraxis Frankfurt</h3>
                  <span className="text-xs text-destructive font-semibold">CEO Fraud</span>
                </div>
              </div>
              <p className="text-sm text-foreground/70 mb-4">
                Betrüger gab sich als Geschäftsführer aus und veranlasste Überweisung von 28.000€.
                Geld konnte nicht zurückgeholt werden.
              </p>
              <div className="pt-4">
                <p className="text-xs text-foreground/60">
                  <strong className="text-destructive">Gesamtschaden:</strong> 28.000€ (direkter Verlust)
                </p>
              </div>
            </div>

          {/* Example 6 */}
          <div className="bg-card border-2 border-destructive/30 rounded-lg card-padding-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm sm:text-base mb-1">Radiologiepraxis Stuttgart</h3>
                  <span className="text-xs text-destructive font-semibold">Spyware-Befall</span>
                </div>
              </div>
              <p className="text-sm text-foreground/70 mb-4">
                Über Monate wurden unbemerkt sensible Daten ausgespäht und im Darknet verkauft.
                470 Patienten mussten informiert werden.
              </p>
              <div className="pt-4">
                <p className="text-xs text-foreground/60">
                  <strong className="text-destructive">Gesamtschaden:</strong> ca. 67.000€ (Forensik + Schadensersatz)
                </p>
              </div>
            </div>
          </div>

        {/* Warning Box */}
        <div className="mt-8 sm:mt-12">
          <CenteredWarningBox icon={AlertTriangle} title="Das Risiko ist real - schützen Sie sich jetzt!" variant="destructive">
            <p className="text-slate-50 text-center text-base font-light">Diese Beispiele zeigen:

Cyberangriffe treffen Praxen jeder Größe. Die durchschnittliche Schadenshöhe liegt bei über 68.451€. Ohne Versicherung kann ein solcher Angriff existenzbedrohend sein.</p>
            
          </CenteredWarningBox>
        </div>
      </ResponsiveSection>

      {/* Protection CTA */}
      <ResponsiveSection variant="accent" spacing="lg" containerSize="md">
        <div className="text-center">
          <Shield className="icon-xl text-accent mx-auto mb-6" />
          <h2 className="heading-2 mb-4 text-foreground">
            Schützen Sie Ihre Praxis jetzt!
          </h2>
          <p className="body-text text-foreground/80 mb-8 max-w-2xl mx-auto">
            Eine Cyberversicherung schützt Sie vor den finanziellen Folgen dieser Bedrohungen 
            und bietet im Ernstfall professionelle Unterstützung.
          </p>
          <div className="flex flex-col sm:flex-row gap-responsive-md justify-center">
            <a href="https://wa.me/491636389945" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-accent/30 hover:bg-accent/10 hover:border-accent text-foreground font-semibold px-6 sm:px-8 py-4 sm:py-5 text-sm sm:text-base transition-all group">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-accent group-hover:scale-110 transition-transform" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:+491636389945" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-accent/30 hover:bg-accent/10 hover:border-accent text-foreground font-semibold px-6 sm:px-8 py-4 sm:py-5 text-sm sm:text-base transition-all group">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-accent group-hover:scale-110 transition-transform" />
                Anrufen
              </Button>
            </a>
            <Link to="/termin-buchen" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-6 sm:px-8 py-4 sm:py-5 text-sm sm:text-base transition-all shadow-lg hover:shadow-accent/20">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Termin buchen
              </Button>
            </Link>
          </div>
        </div>
      </ResponsiveSection>

      {/* Related Links Section */}
      <ResponsiveSection spacing="lg" containerSize="lg">
        <div className="bg-card/30 backdrop-blur-sm rounded-xl p-8 border border-border/50">
          <h2 className="heading-3 mb-6 text-center text-foreground">Mehr erfahren</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/tarifwissen" className="group p-6 rounded-lg bg-card/50 border border-border/30 hover:border-accent/50 transition-all hover:shadow-lg">
              <Shield className="w-8 h-8 text-accent mb-3" />
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-accent transition-colors">Tarifvergleich</h3>
              <p className="text-sm text-muted-foreground">Alle Cyberversicherungen im Überblick</p>
            </Link>
            <Link to="/cyberversicherung" className="group p-6 rounded-lg bg-card/50 border border-border/30 hover:border-accent/50 transition-all hover:shadow-lg">
              <Database className="w-8 h-8 text-accent mb-3" />
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-accent transition-colors">Regionale Beratung</h3>
              <p className="text-sm text-muted-foreground">Cyberversicherung in Ihrer Stadt</p>
            </Link>
            <Link to="/faq" className="group p-6 rounded-lg bg-card/50 border border-border/30 hover:border-accent/50 transition-all hover:shadow-lg">
              <MessageSquare className="w-8 h-8 text-accent mb-3" />
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-accent transition-colors">FAQ</h3>
              <p className="text-sm text-muted-foreground">Häufig gestellte Fragen</p>
            </Link>
          </div>
        </div>
      </ResponsiveSection>

      <ExpertSection />
      <CTASection title="Schützen Sie Ihre Praxis vor Cyberrisiken" subtitle="Kostenlose Beratung zu allen Cyberversicherungen" />
      
      <Footer />
      </div>
    </div>;
};
export default CyberRisks;